export const OPPORTUNITY_STATUSES = [
  'captured',
  'under_analysis',
  'approved',
  'realized',
  'deferred',
  'dismissed',
] as const;

export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];

export const OPPORTUNITY_LEVELS = ['low', 'medium', 'high'] as const;

export type OpportunityLevel = (typeof OPPORTUNITY_LEVELS)[number];

export const OPPORTUNITY_SOURCE_KINDS = [
  'manual',
  'gemba',
  'vsm',
  'indicator',
  'problem',
  'chronoanalysis',
  'gbo',
  'flow_designer',
] as const;

export type OpportunitySourceKind = (typeof OPPORTUNITY_SOURCE_KINDS)[number];

export type DerivedPriority =
  | 'do_now'
  | 'evaluate_cost'
  | 'plan'
  | 'when_spare'
  | 'not_assessed';

export interface Opportunity {
  id: string;
  organization_id: string;
  title: string;
  description: string | null;
  status: OpportunityStatus;
  category: string;
  impact_level: OpportunityLevel | null;
  effort_level: OpportunityLevel | null;
  source_kind: OpportunitySourceKind;
  source_issue_id: string | null;
  source_indicator_id: string | null;
  source_project_id: string | null;
  source_ref_id: string | null;
  source_label: string | null;
  source_context: Record<string, unknown>;
  owner_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Nome resolvido em user_profiles. Não é coluna de opportunities. */
  owner_name?: string | null;
}

export interface OpportunityCreateInput {
  title: string;
  description?: string | null;
  category?: string | null;
  impact_level?: OpportunityLevel | null;
  effort_level?: OpportunityLevel | null;
  owner_id?: string | null;
  created_by?: string | null;
  source_kind?: OpportunitySourceKind;
  source_issue_id?: string | null;
  source_label?: string | null;
  source_context?: Record<string, unknown> | null;
}

export interface OpportunityUpdateInput {
  title?: string;
  description?: string | null;
  category?: string;
  impact_level?: OpportunityLevel | null;
  effort_level?: OpportunityLevel | null;
  owner_id?: string | null;
  status?: OpportunityStatus;
}

export const OPPORTUNITY_STATUS_LABELS: Record<OpportunityStatus, string> = {
  captured: 'Capturada',
  under_analysis: 'Em análise',
  approved: 'Aprovada',
  realized: 'Realizada',
  deferred: 'Adiada',
  dismissed: 'Descartada',
};

export const OPPORTUNITY_LEVEL_LABELS: Record<OpportunityLevel, string> = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
};

export const OPPORTUNITY_SOURCE_LABELS: Record<OpportunitySourceKind, string> = {
  manual: 'Manual',
  gemba: 'Gemba',
  vsm: 'VSM',
  indicator: 'Indicador',
  problem: 'Problema',
  chronoanalysis: 'Cronoanálise',
  gbo: 'GBO',
  flow_designer: 'Flow Designer',
};

export const DERIVED_PRIORITY_LABELS: Record<DerivedPriority, string> = {
  do_now: 'Fazer agora',
  evaluate_cost: 'Avaliar custo',
  plan: 'Planejar',
  when_spare: 'Quando houver folga',
  not_assessed: 'Não avaliada',
};

const PRIORITY_RANK: Record<DerivedPriority, number> = {
  do_now: 0,
  evaluate_cost: 1,
  plan: 2,
  when_spare: 3,
  not_assessed: 4,
};

export const OPPORTUNITY_STATUS_BADGE: Record<OpportunityStatus, string> = {
  captured: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
  under_analysis: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  approved: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  realized: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  deferred: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  dismissed: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
};

export const DERIVED_PRIORITY_BADGE: Record<DerivedPriority, string> = {
  do_now: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  evaluate_cost: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  plan: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  when_spare: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  not_assessed: 'bg-slate-500/10 text-slate-500 dark:text-slate-400',
};

export const OPPORTUNITY_LEVEL_BADGE: Record<OpportunityLevel, string> = {
  high: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  medium: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  low: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
};

export function categoryLabel(category: string | null | undefined): string {
  const value = category?.trim();
  if (!value || value === 'other') return 'Outra';
  return value;
}

export function deriveOpportunityPriority(
  impact: OpportunityLevel | null | undefined,
  effort: OpportunityLevel | null | undefined,
): DerivedPriority {
  if (!impact || !effort) return 'not_assessed';
  const key = `${impact}:${effort}`;
  switch (key) {
    case 'high:low':
    case 'high:medium':
    case 'medium:low':
      return 'do_now';
    case 'high:high':
      return 'evaluate_cost';
    case 'medium:medium':
    case 'low:low':
      return 'plan';
    case 'medium:high':
    case 'low:medium':
    case 'low:high':
      return 'when_spare';
    default:
      return 'not_assessed';
  }
}

export function compareOpportunitiesByPriority(left: Opportunity, right: Opportunity): number {
  const rank =
    PRIORITY_RANK[deriveOpportunityPriority(left.impact_level, left.effort_level)] -
    PRIORITY_RANK[deriveOpportunityPriority(right.impact_level, right.effort_level)];
  if (rank !== 0) return rank;
  return Date.parse(right.updated_at) - Date.parse(left.updated_at);
}

export interface OpportunityDecision {
  status: OpportunityStatus;
  label: string;
}

export function opportunityDecisions(status: OpportunityStatus): OpportunityDecision[] {
  switch (status) {
    case 'captured':
      return [
        { status: 'under_analysis', label: 'Colocar em análise' },
        { status: 'approved', label: 'Aprovar' },
        { status: 'deferred', label: 'Adiar' },
        { status: 'dismissed', label: 'Descartar' },
      ];
    case 'under_analysis':
      return [
        { status: 'approved', label: 'Aprovar' },
        { status: 'deferred', label: 'Adiar' },
        { status: 'dismissed', label: 'Descartar' },
      ];
    case 'approved':
      return [
        { status: 'deferred', label: 'Adiar' },
        { status: 'dismissed', label: 'Descartar' },
      ];
    case 'deferred':
      return [
        { status: 'under_analysis', label: 'Colocar em análise' },
        { status: 'approved', label: 'Aprovar' },
        { status: 'dismissed', label: 'Descartar' },
      ];
    case 'dismissed':
      return [{ status: 'under_analysis', label: 'Colocar em análise' }];
    case 'realized':
      return [];
  }
}

export function usefulSourceContext(context: Record<string, unknown> | null | undefined): [string, string][] {
  if (!context || typeof context !== 'object' || Array.isArray(context)) return [];
  const entries: [string, string][] = [];
  for (const [key, value] of Object.entries(context)) {
    if (value == null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0) continue;
    const text = typeof value === 'string' ? value : JSON.stringify(value);
    if (!text) continue;
    entries.push([key, text]);
    if (entries.length >= 8) break;
  }
  return entries;
}
